/*
 * toast.js — the shared transient-message component (B2C-284).
 *
 * Knows nothing about carts, ceilings or any other feature. Consumers depend
 * on it; it depends on nobody. That direction is deliberate — it is the only
 * one that keeps the toast usable by a feature with no cart in it.
 *
 * window.CasinettoToast.show({ variant, role, message, lines })
 *   variant — 'critical' | 'info'   (default 'info')
 *   role    — 'alert' | 'status'    (default: 'alert' for critical, else 'status')
 *   message — a resolved, translated string (optional when `lines` is given)
 *   lines   — array of strings, or of { title, text } objects, rendered as
 *             item rows under the message. Used by the bulk callers.
 *
 * window.CasinettoToast.dismiss()
 *
 * ONE TOAST AT A TIME. A second show replaces the first with no visible gap,
 * so a fast tapper gets the latest fact rather than a stack.
 *
 * All copy arrives already translated. No English lives in this file —
 * CONSTITUTION §IV.13. The only string it reads is the dismiss label, from
 * `data-label-dismiss` on the host (snippets/molecule-toast.liquid).
 *
 * The icons are cloned from the host's <template> blocks. Do not reintroduce
 * SVG path strings here: that is how this codebase ended up with two trash
 * icons (decisions/quantity-ceilings-structure.md D3).
 */
(function () {
  'use strict';

  var BASE_MS = 6000;
  var PER_LINE_MS = 1200;
  var MAX_MS = 12000;
  var EXIT_MS = 400; /* transition fallback; the CSS exit is --t-hover (120ms) */

  var root = null;
  var hosts = {};
  var templates = {};
  var dismissLabel = '';

  /* The one live toast, and everything that has to be torn down with it. */
  var current = null; /* { el, host, controller, timer, duration, lineCount } */

  function prefersReducedMotion() {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init() {
    root = document.querySelector('[data-toast-root]');
    if (!root) { return false; }
    hosts.alert = root.querySelector('[data-toast-host="alert"]');
    hosts.status = root.querySelector('[data-toast-host="status"]');
    templates.critical = root.querySelector('[data-toast-icon="critical"]');
    templates.info = root.querySelector('[data-toast-icon="info"]');
    templates.dismiss = root.querySelector('[data-toast-icon="dismiss"]');
    dismissLabel = root.dataset.labelDismiss || '';
    return !!(hosts.alert && hosts.status);
  }

  function cloneIcon(name, className) {
    var tpl = templates[name];
    var wrap = document.createElement('span');
    wrap.className = className;
    if (tpl && tpl.content) {
      wrap.appendChild(tpl.content.cloneNode(true));
    }
    return wrap;
  }

  /* ---- Build ----------------------------------------------------------- */

  function buildToast(opts) {
    var variant = opts.variant === 'critical' ? 'critical' : 'info';

    var el = document.createElement('div');
    el.className = 'c-toast c-toast--' + variant;
    el.setAttribute('data-toast', '');

    el.appendChild(cloneIcon(variant, 'c-toast__icon'));

    var content = document.createElement('div');
    content.className = 'c-toast__content';

    var lineCount = 0;

    if (opts.message) {
      var p = document.createElement('p');
      p.className = 'c-toast__message';
      p.textContent = opts.message;
      content.appendChild(p);
      lineCount += 1;
    }

    if (opts.lines && opts.lines.length) {
      var list = document.createElement('ul');
      list.className = 'c-toast__list';
      for (var i = 0; i < opts.lines.length; i++) {
        var line = opts.lines[i];
        var li = document.createElement('li');
        li.className = 'c-toast__item';
        if (line && typeof line === 'object') {
          if (line.title) {
            var title = document.createElement('span');
            title.className = 'c-toast__item-title';
            title.textContent = line.title;
            li.appendChild(title);
          }
          if (line.text) {
            var reason = document.createElement('span');
            reason.className = 'c-toast__item-reason';
            reason.textContent = line.text;
            li.appendChild(reason);
          }
        } else {
          var plain = document.createElement('span');
          plain.className = 'c-toast__item-reason';
          plain.textContent = String(line);
          li.appendChild(plain);
        }
        list.appendChild(li);
        lineCount += 1;
      }
      content.appendChild(list);
    }

    el.appendChild(content);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'c-toast__dismiss';
    btn.setAttribute('data-toast-dismiss', '');
    if (dismissLabel) { btn.setAttribute('aria-label', dismissLabel); }
    btn.appendChild(cloneIcon('dismiss', 'c-toast__dismiss-glyph'));
    el.appendChild(btn);

    return { el: el, lineCount: lineCount };
  }

  /* ---- Timers ---------------------------------------------------------- */

  function durationFor(lineCount) {
    var extra = Math.max(0, lineCount - 1) * PER_LINE_MS;
    return Math.min(MAX_MS, BASE_MS + extra);
  }

  function startTimer(entry) {
    clearTimeout(entry.timer);
    entry.timer = setTimeout(function () {
      if (current === entry) { dismiss(); }
    }, entry.duration);
  }

  function pauseTimer(entry) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }

  /* ---- Teardown -------------------------------------------------------- */

  /* Every listener this toast owns — its own hover/focus pair and the
     document-level Escape handler — hangs off one AbortController, so a
     dismiss cannot leave one behind. The auto-dismiss timer is cleared in
     the same place for the same reason. */
  function teardown(entry) {
    if (!entry) { return; }
    clearTimeout(entry.timer);
    entry.timer = null;
    if (entry.controller) { entry.controller.abort(); }
  }

  function removeNow(entry) {
    teardown(entry);
    if (entry.el && entry.el.parentNode) {
      entry.el.parentNode.removeChild(entry.el);
    }
  }

  function dismiss() {
    var entry = current;
    if (!entry) { return; }
    current = null;
    teardown(entry);

    if (!entry.el || !entry.el.parentNode) { return; }

    entry.el.setAttribute('data-exit', '');
    var done = false;
    var finish = function () {
      if (done) { return; }
      done = true;
      if (entry.el && entry.el.parentNode) {
        entry.el.parentNode.removeChild(entry.el);
      }
    };
    entry.el.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, EXIT_MS);
  }

  /* ---- Show ------------------------------------------------------------ */

  function show(opts) {
    if (!root && !init()) { return; }
    if (!opts) { return; }
    if (!opts.message && !(opts.lines && opts.lines.length)) { return; }

    var variant = opts.variant === 'critical' ? 'critical' : 'info';
    var role = opts.role;
    if (role !== 'alert' && role !== 'status') {
      role = variant === 'critical' ? 'alert' : 'status';
    }
    var host = hosts[role];
    if (!host) { return; }

    /* Height of what is on screen, measured BEFORE it leaves, so the new
       toast can morph from it instead of jumping. */
    var previousHeight = 0;
    if (current && current.el && current.el.parentNode) {
      previousHeight = current.el.offsetHeight;
    }

    /* Clear BOTH regions and repopulate on the next frame. Clearing and
       repopulating in one frame is the reliable way to get a screen reader
       to miss the second message entirely. */
    if (current) { removeNow(current); current = null; }
    hosts.alert.textContent = '';
    hosts.status.textContent = '';

    var built = buildToast({ variant: variant, message: opts.message, lines: opts.lines });
    var controller = new AbortController();
    var entry = {
      el: built.el,
      host: host,
      controller: controller,
      timer: null,
      duration: durationFor(built.lineCount),
      lineCount: built.lineCount
    };
    current = entry;

    var isReplace = previousHeight > 0;
    built.el.setAttribute(isReplace ? 'data-replace' : 'data-enter', '');

    /* Two shows in one frame each schedule their own frame; the stale one
       bails on this identity check and the last fact wins. */
    requestAnimationFrame(function () {
      if (current !== entry) { return; }

      host.appendChild(entry.el);
      bindToast(entry, controller.signal);

      var reduced = prefersReducedMotion();
      if (isReplace && !reduced) {
        var target = entry.el.offsetHeight;
        entry.el.style.blockSize = previousHeight + 'px';
        entry.el.style.overflow = 'hidden';
        /* Force a reflow so the start height is committed before the
           transition to the new one. */
        void entry.el.offsetHeight;
        entry.el.removeAttribute('data-replace');
        entry.el.style.blockSize = target + 'px';
        var clearSize = function () {
          entry.el.style.blockSize = '';
          entry.el.style.overflow = '';
        };
        entry.el.addEventListener('transitionend', function (e) {
          if (e.propertyName === 'block-size' || e.propertyName === 'height') { clearSize(); }
        }, { signal: controller.signal });
        setTimeout(clearSize, EXIT_MS);
      } else {
        /* Force a reflow so the pre-state paints before it is removed. */
        void entry.el.offsetHeight;
        entry.el.removeAttribute('data-enter');
        entry.el.removeAttribute('data-replace');
      }

      startTimer(entry);
    });
  }

  function bindToast(entry, signal) {
    entry.el.addEventListener('click', function (e) {
      if (e.target.closest('[data-toast-dismiss]')) { dismiss(); }
    }, { signal: signal });

    /* A timer alone fails anyone reading slowly, and a pointer or a caret
       resting on the message is the signal that they are. Restart at full
       duration on leave rather than resuming the remainder — the reader has
       just re-engaged with it. */
    entry.el.addEventListener('mouseenter', function () { pauseTimer(entry); }, { signal: signal });
    entry.el.addEventListener('focusin', function () { pauseTimer(entry); }, { signal: signal });
    entry.el.addEventListener('mouseleave', function () {
      if (current === entry) { startTimer(entry); }
    }, { signal: signal });
    entry.el.addEventListener('focusout', function () {
      if (current === entry) { startTimer(entry); }
    }, { signal: signal });

    /* Escape, on CAPTURE, stopping propagation while a toast is showing:
       one press dismisses the toast WITHOUT also closing an open cart
       drawer. The topmost transient thing goes first. Removed with the rest
       of this toast's listeners when it goes away. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') { return; }
      if (current !== entry) { return; }
      e.stopPropagation();
      dismiss();
    }, { capture: true, signal: signal });
  }

  window.CasinettoToast = {
    show: show,
    dismiss: dismiss
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
